import { useMemo } from "react";
import { useAuth } from "../auth/authContext.tsx";

interface UsePermissionsResult {
  canRead: boolean;
  canWrite: boolean;
}

export function usePermissions(pageRowId: number): UsePermissionsResult {
  const { canRead, canWrite } = useAuth();
  return {
    canRead: canRead(pageRowId),
    canWrite: canWrite(pageRowId),
  };
}

const OPERATION_TYPES = new Set(["VIEW", "FN", "SP", "EXEC", "IMP"]);

/**
 * Check whether the current user has read permission for one or more grid names.
 * Returns a lookup keyed by grid name (lowercase).
 */
export function useGridPermissions(gridNames: string[]): Record<string, boolean> {
  const { cache, canRead } = useAuth();
  return useMemo(() => {
    const result: Record<string, boolean> = {};
    for (const name of gridNames) {
      const nameLower = name.toLowerCase();
      if (!cache?.PageInfo?.Page) {
        result[nameLower] = false;
        continue;
      }
      let resolved: (typeof cache.PageInfo.Page)[number] | undefined;
      for (const p of cache.PageInfo.Page) {
        if (p.Name?.toLowerCase() !== nameLower) continue;
        if (!p.Enabled || !p.Available) continue;
        if (!OPERATION_TYPES.has(p.Type?.toUpperCase())) continue;
        resolved = p;
      }
      result[nameLower] = resolved ? canRead(resolved.RowID) : false;
    }
    return result;
  }, [cache?.PageInfo, canRead, gridNames]);
}
